import React from 'react';
import mapValues from 'lodash/mapValues';
import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';
import { useQueryParams, StringParam, BooleanParam } from 'use-query-params';

import { throttle } from 'lodash';
import { SocketProvider } from './providers/SocketProvider';
import { ShareDBProvider } from './providers/ShareDBProvider';
import { WEBSOCKET_SHAREDB_HOST } from './config';
import Workbook from './views/Workbook';
import ForumDoc from './views/ForumDoc';
import { MissingQueryParametersError } from './errors';
import './css/external.css';
import {
  postDocumentScrollHeightMessage,
  postPresentationModeConnectMessage,
  postWindowScrollMessage,
  WINDOW_SHOULD_SCROLL_MESSAGE,
} from './helpers/parent-messaging';

const ForumCodeDoc = React.lazy(() => import('./views/ForumCodeDoc'));

const handleKeyDown = (e) => {
  if (e.key === 'Control') {
    window.parent.postMessage('SLED.ctrlKey.keyDown', '*');
  }
};

const handleKeyUp = (e) => {
  if (e.key === 'Control') {
    window.parent.postMessage('SLED.ctrlKey.keyUp', '*');
  }
};

// We throttle the resize command as it is not critical to be constantly in sync when the content is iframed,
// though the options ensure the final value gets sent (which is important).
const handleResize = throttle(
  () => {
    postDocumentScrollHeightMessage();
  },
  50,
  { trailing: true, leading: true }
);

// The throttle period for handleScroll may need to be tweaked in the future to ensure it feels responsive
// while not spamming the parent window with messages.
const handleScroll = (documentId) =>
  throttle(
    () => {
      postWindowScrollMessage(documentId);
    },
    200,
    { trailing: true, leading: true }
  );

const handleParentMessage = (message) => {
  if (message.data.type === WINDOW_SHOULD_SCROLL_MESSAGE) {
    window.scroll({
      left: message.data.windowScrollX,
      top: message.data.windowScrollY,
      behavior: 'smooth',
    });
  }
};

const MANDATORY_QUERY_PARAMETERS = {
  name: 'user name',
  userId: '',
};

export const CollaborationView = () => {
  const params = {
    url: StringParam,
    name: StringParam,
    userId: StringParam,
    avatar: StringParam,
    noPresence: BooleanParam,
    templateId: StringParam,
    readOnly: BooleanParam,
    iframed: BooleanParam,
    timeline: BooleanParam,
    signature: StringParam,
    id: StringParam,
    minervaDocId: StringParam,
    minervaNotebookId: StringParam,
    rollup: StringParam,
    isInstructor: BooleanParam,
    isGrading: BooleanParam,
    isAdmin: BooleanParam,
    enableSavingIndicators: BooleanParam,
    captionsContent: BooleanParam,
    darkMode: BooleanParam,
    noWhitespace: BooleanParam,
    bannerMessage: StringParam,
  };

  const [query] = useQueryParams(params);
  const {
    url,
    name,
    userId,
    avatar,
    readOnly,
    iframed,
    noPresence,
    id,
    templateId,
    minervaDocId,
    minervaNotebookId,
    rollup,
    timeline,
    isInstructor,
    isGrading,
    isAdmin,
    enableSavingIndicators,
    captionsContent,
    darkMode,
    noWhitespace,
    bannerMessage,
  } = query;

  const missingQueryParameters = pickBy(
    MANDATORY_QUERY_PARAMETERS,
    (value, key) => query[key] === undefined
  );

  let messages = {};
  const error =
    !isEmpty(missingQueryParameters) ||
    !(url || id || templateId || minervaDocId || minervaNotebookId);
  if (error) {
    messages = mapValues(missingQueryParameters, (message, key) => {
      if (message) {
        return `${key} (${message})`;
      }
      return key;
    });
    if (!(url || id || minervaDocId || minervaNotebookId)) {
      messages.url =
        'either url (worksheet or worksheet template url) or minervaDocId or minervaNotebookId';
    }
    console.error(
      new MissingQueryParametersError(
        `Missing query parameters: ${Object.values(messages).join(', ')}`
      )
    );
  }

  // we decided to have readOnly always trigger noPresence for now, since it is weird
  // to see cursors when you cannot edit etc. We might in the future decide to change this,
  // we could even separate general document level presence from shared cursors
  const derivedNoPresence = noPresence || readOnly;

  const user = {
    name,
    userId,
    avatar,
    readOnly,
    iframed,
    rollup,
    timeline,
    isInstructor,
    isGrading,
    isAdmin,
    enableSavingIndicators,
    noPresence: derivedNoPresence,
  };

  if (window.Rollbar) {
    window.Rollbar.configure({
      verbose: true,
      checkIgnore: (isUncaught, args, payload) => {
        // Ignore https://rollbar.com/minervaproject/SLE/items/890/ and https://rollbar.com/minervaproject/SLE/items/889/
        // because the logs can't easily be disabled, but are also not actionable and clog rollbar.
        const body = payload.body?.message?.body || '';
        return (
          body.includes('Websocket closed with wasClean false') ||
          body.includes('Websocket error')
        );
      },
      transform: (payload) => {
        // Remove redundant information from payload for readability in the web interface
        if (payload.request.url && payload.request.query_string) {
          delete payload.request.query_string;
        }
      },
      payload: {
        person: {
          id: userId,
          username: name,
        },
        readOnly,
        noPresence,
        url,
        iframed,
        rollup,
        isGrading,
      },
    });
  }

  const SOCKET_URLS = {
    sharedb: `${WEBSOCKET_SHAREDB_HOST}/socket/sharedb${window.location.search}`,
  };

  const secure = window.location.protocol === 'https:';

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('resize', handleResize);
    handleResize();
  }, []);

  React.useEffect(() => {
    const onScroll = handleScroll(id || minervaDocId || minervaNotebookId);
    if (isInstructor) {
      // We need to keep track of what document we are in based on the id
      // (which different variables are used between Forum Docs, Code Docs,
      // and Workbooks), but it's not necessary to send the document type.
      window.addEventListener('scroll', onScroll);
    }
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [isInstructor, id, minervaDocId, minervaNotebookId]);

  // This functionality is currently only used in the instructor presentation view in Forum,
  // but it is possible we may expand its use in the future.
  React.useEffect(() => {
    // Some browsers may require a message sent from the child to the parent before the parent
    // can send messages to the child (see comment on 'SLED.grading.connect' for more info).
    postPresentationModeConnectMessage(id || minervaDocId || minervaNotebookId);
    window.addEventListener('message', handleParentMessage);
    return () => {
      window.removeEventListener('message', handleParentMessage);
    };
  }, [id, minervaDocId, minervaNotebookId]);

  const style = darkMode ? { minHeight: '100vh' } : {};
  const classNames = `minerva-resource primary-font ${
    darkMode ? 'bg-black-tint-10' : ''
  }`;

  if (error) {
    return (
      <div style={{ width: '500px', margin: '0 auto' }}>
        <h1 className="h1">Document not found - 404</h1>
        <div className="body">
          The document might no longer exist, or you might not be allowed to
          access it. Please visit the document again through Forum.
        </div>
      </div>
    );
  }

  return (
    <SocketProvider sockets={SOCKET_URLS} secure={secure}>
      <ShareDBProvider>
        <React.Suspense fallback={null}>
          <div className={classNames} style={style}>
            {minervaDocId ? (
              <ForumDoc
                minervaDocId={minervaDocId}
                currentUser={user}
                captionsContent={captionsContent}
                darkMode={darkMode}
                noWhitespace={noWhitespace}
                bannerMessage={bannerMessage}
              />
            ) : minervaNotebookId ? (
              <ForumCodeDoc
                minervaNotebookId={minervaNotebookId}
                currentUser={user}
                bannerMessage={bannerMessage}
              />
            ) : (
              <Workbook
                url={url}
                templateId={templateId}
                id={id}
                currentUser={user}
                bannerMessage={bannerMessage}
              />
            )}
          </div>
        </React.Suspense>
      </ShareDBProvider>
    </SocketProvider>
  );
};
